import { Grid, Card, CardHeader, CardContent, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { Engineering, Verified, Bolt, Hotel, Speed, Timer, AccountBalanceWallet, TrendingUp } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../../components/PageHeader';
import { StatCard } from '../../../components/StatCard';
import { MiniBarChart } from '../../../components/MiniBarChart';
import { EmptyState } from '../../../components/EmptyState';
import { technicianDashboardApi } from '../../../api/technicianOnboarding.api';

const n = (x = 0) => Number(x).toLocaleString('en-IN');

function ChartCard({ title, data }: { title: string; data: Array<{ label: string; value: number }> }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader title={title} titleTypographyProps={{ variant: 'subtitle1' }} />
      <CardContent>{data?.length ? <MiniBarChart data={data.map((d) => ({ ...d, color: '#1e40af' }))} /> : <EmptyState title="No data yet" dense />}</CardContent>
    </Card>
  );
}

export function TechnicianOnboardingDashboardPage() {
  const { data } = useQuery({ queryKey: ['technician-onboarding-dashboard'], queryFn: () => technicianDashboardApi.dashboard() });
  const k = data?.kpis ?? {};
  const c = data?.charts ?? {};

  return (
    <>
      <PageHeader title="Technician Dashboard" subtitle="Workforce pipeline, deployment and performance analytics." />

      <Grid container spacing={2} mb={1}>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#1e40af" label="Total Technicians" value={n(k.totalTechnicians)} icon={<Engineering />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#16a34a" label="Active" value={n(k.activeTechnicians)} icon={<Verified />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#0d9488" label="Deployed" value={n(k.deployedTechnicians)} icon={<Bolt />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#d97706" label="Idle" value={n(k.idleTechnicians)} icon={<Hotel />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#7c3aed" label="On Leave" value={n(k.onLeave)} icon={<Hotel />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#0284c7" label="SLA Achievement" value={`${n(k.slaAchievement)}%`} icon={<Speed />} /></Grid>
      </Grid>
      <Grid container spacing={2} mb={1}>
        <Grid item xs={6} sm={4}><StatCard color="#1e40af" label="Average TAT (hrs)" value={n(k.averageTat)} icon={<Timer />} /></Grid>
        <Grid item xs={6} sm={4}><StatCard color="#dc2626" label="Pending Payments" value={`₹${n(k.pendingPayments)}`} icon={<AccountBalanceWallet />} /></Grid>
        <Grid item xs={6} sm={4}><StatCard color="#16a34a" label="SLA Achievement" value={`${n(k.slaAchievement)}%`} icon={<TrendingUp />} /></Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}><ChartCard title="By Application Status" data={c.techniciansByStatus ?? []} /></Grid>
        <Grid item xs={12} md={4}><ChartCard title="By Operational Status" data={c.techniciansByOperationalStatus ?? []} /></Grid>
        <Grid item xs={12} md={4}><ChartCard title="By Type" data={c.techniciansByType ?? []} /></Grid>
        <Grid item xs={12} md={4}><ChartCard title="By Experience Level" data={c.techniciansByExperienceLevel ?? []} /></Grid>
        <Grid item xs={12} md={4}><ChartCard title="Top Performers (SLA %)" data={c.topPerformers ?? []} /></Grid>

        <Grid item xs={12} md={6}>
          <Card><CardHeader title="Top Performers" titleTypographyProps={{ variant: 'subtitle1' }} />
            <CardContent sx={{ p: 0, overflowX: 'auto' }}>
              {(data?.tables.topPerformers.length ?? 0) === 0 ? <EmptyState title="None" dense /> : (
                <Table size="small"><TableHead><TableRow><TableCell>Technician</TableCell><TableCell>SLA %</TableCell><TableCell>Sites Done</TableCell><TableCell>Rating</TableCell></TableRow></TableHead>
                  <TableBody>{data!.tables.topPerformers.map((r, i) => <TableRow key={i}><TableCell>{String(r.technicianName)}</TableCell><TableCell>{String(r.sla ?? '—')}</TableCell><TableCell>{String(r.sitesCompleted ?? 0)}</TableCell><TableCell>{String(r.rating ?? '—')}</TableCell></TableRow>)}</TableBody></Table>
              )}
            </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card><CardHeader title="Recently Submitted" titleTypographyProps={{ variant: 'subtitle1' }} />
            <CardContent sx={{ p: 0, overflowX: 'auto' }}>
              {(data?.tables.recentlySubmitted.length ?? 0) === 0 ? <EmptyState title="None" dense /> : (
                <Table size="small"><TableHead><TableRow><TableCell>Technician ID</TableCell><TableCell>Name</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
                  <TableBody>{data!.tables.recentlySubmitted.map((r, i) => <TableRow key={i}><TableCell>{String(r.applicationCode)}</TableCell><TableCell>{String(r.technicianName)}</TableCell><TableCell>{String(r.status).replace(/_/g, ' ')}</TableCell></TableRow>)}</TableBody></Table>
              )}
            </CardContent></Card>
        </Grid>
      </Grid>
    </>
  );
}
